import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { CareerCategory, CareerPath, CareerPathCard } from '@backrs_ui/common-ui';

import { ReactComponent as NoStrongFitImage } from '@backrs_ui/grow-ui/public/images/noun-map-confusion.svg';
import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CareerPathWithScore } from '@backrs_ui/grow-ui/src/models/models';
import { CareerCategorySegments } from '@backrs_ui/grow-ui/src/pages/CareerPath/molecules/careerPathPage/CareerCategorySegments';
import {
  sortCareerCategories,
  filterCareerPathsByCategory,
  getCareerPathCardData,
} from '@backrs_ui/grow-ui/src/pages/CareerPath/util';

import { Box, SxProps, Theme, Button, Collapse, Typography } from '@mui/material';

import { FitPathsLegend } from './FitPathsLegend';

const STRINGS = appStrings.modules.careerPath.summaryPage.strongFitPaths;

type BlueprintStrongFitPathProps = Readonly<{
  careerCategories: CareerCategory[];
  careerPaths: CareerPath[];
  careerPathSuggestions: CareerPathWithScore[] | undefined;
  isShared?: boolean;
  onCareerPathSelect?: (careerPath: CareerPath) => void;
}>;

const INITIAL_CAREER_PATHS_TO_SHOW = 6;

export const BlueprintStrongFitPath = ({
  careerCategories,
  careerPathSuggestions,
  isShared = false,
  onCareerPathSelect,
}: BlueprintStrongFitPathProps) => {
  const sortedCareerCategories = sortCareerCategories(careerCategories, careerPathSuggestions);
  const params = useParams();
  const sharedSessionUUID = params.uuid;

  const [showAllPaths, setShowAllPaths] = useState(false);
  // Default to the first category being selected for better UX
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | undefined>(
    sortedCareerCategories.length > 0 ? sortedCareerCategories[0].id.toString() : undefined,
  );
  const navigate = useNavigate();

  const filteredCareerPaths = filterCareerPathsByCategory(
    selectedCategoryId,
    careerPathSuggestions,
  );

  const handleCareerPathClick = (careerPath: CareerPath) => {
    if (onCareerPathSelect) {
      onCareerPathSelect(careerPath);

      return;
    }

    if (isShared) {
      navigate(
        `${APP_ROUTE.SHARED_CAREER_PATHS.replace(':uuid', sharedSessionUUID || '')}/${careerPath.id}`,
      );
    } else {
      navigate(`${APP_ROUTE.CAREER_PATHS}/${careerPath.id}`);
    }
  };

  const handleSeeMoreClick = () => {
    setShowAllPaths(true);
  };

  const handleSeeLessClick = () => {
    setShowAllPaths(false);
  };

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategoryId(categoryId === selectedCategoryId ? undefined : categoryId);
    setShowAllPaths(false); // Reset to show initial count when switching categories
  };

  const displayedPaths = showAllPaths
    ? filteredCareerPaths
    : filteredCareerPaths.slice(0, INITIAL_CAREER_PATHS_TO_SHOW);
  const hasMorePaths = filteredCareerPaths.length > INITIAL_CAREER_PATHS_TO_SHOW;

  const NoPaths = (
    <Box sx={noPathsContainerSx}>
      <Typography
        color={(theme) => theme.backrs.colors['Colors/Foreground/fg-quaternary']}
        variant="text-sm-medium"
      >
        {STRINGS.noStrongOrMediumCareerFits}
      </Typography>
      <NoStrongFitImage />
    </Box>
  );

  return (
    <Box>
      {/* Legend */}
      <Box sx={legendSx}>
        <FitPathsLegend />
      </Box>

      {/* chart segments + path grids */}
      <Box sx={segmentAndGridContainerSx}>
        {/* Career Category Segments */}
        <Box sx={segmentsContainerStyle}>
          <CareerCategorySegments
            careerCategories={sortedCareerCategories}
            careerPathSuggestions={careerPathSuggestions}
            onCategorySelect={handleCategorySelect}
            selectedCategoryId={selectedCategoryId}
          />
        </Box>

        {/* Career Paths Grid */}
        <Box sx={{ flex: 1 }}>
          {displayedPaths.length > 0 ? (
            <Box sx={careerPathsGridStyle}>
              {displayedPaths.map((careerPath, index) => {
                const { categoryIcon, fitLevel, fitColor, shortName } = getCareerPathCardData({
                  careerPath,
                  careerPathSuggestions,
                  sortedCareerCategories,
                });
                const isAdditionalCard = index >= INITIAL_CAREER_PATHS_TO_SHOW;

                const card = (
                  <CareerPathCard
                    id={`career-path-fit-card-${careerPath.id}`}
                    key={careerPath.id}
                    iconName={categoryIcon}
                    careerPathName={careerPath.name}
                    careerCategoryName={shortName}
                    onClick={() => handleCareerPathClick(careerPath)}
                    fitLevel={fitLevel}
                    fitColor={fitColor}
                  />
                );

                return isAdditionalCard ? (
                  <Collapse key={careerPath.id} in={showAllPaths} timeout={300}>
                    <Box
                      sx={{
                        ...animatedCardStyle,
                        animationDelay: `${(index - INITIAL_CAREER_PATHS_TO_SHOW) * 0.1}s`,
                      }}
                    >
                      {card}
                    </Box>
                  </Collapse>
                ) : (
                  card
                );
              })}
            </Box>
          ) : (
            NoPaths
          )}
        </Box>
      </Box>

      {hasMorePaths && (
        <Box sx={seeMoreContainerStyle}>
          {!showAllPaths ? (
            <Button
              id="career-path-show-all-button"
              variant="text"
              size="sm"
              onClick={handleSeeMoreClick}
              sx={seeMoreButtonStyle}
            >
              {STRINGS.showAll}
            </Button>
          ) : (
            <Button
              id="career-path-show-less-button"
              variant="text"
              size="sm"
              onClick={handleSeeLessClick}
              sx={seeMoreButtonStyle}
            >
              {STRINGS.showLess}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

const careerPathsGridStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(250px, 100%), 1fr))',
  gap: backrs.spacing['spacing-3xl'],
  width: '100%',
  minWidth: 0,
  '& > *': {
    width: '100%',
    minWidth: 0,
  },
});

const animatedCardStyle: SxProps<Theme> = {
  opacity: 0,
  transform: 'translateY(20px)',
  animation: 'fadeInUp 0.3s ease-out forwards',
  width: '100%',
  minWidth: 0,
  '@keyframes fadeInUp': {
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
} as const;

const seeMoreContainerStyle: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-end',
} as const;

const seeMoreButtonStyle: SxProps<Theme> = {
  textTransform: 'none',
} as const;

const segmentsContainerStyle: SxProps<Theme> = {
  flexShrink: 0,
  minWidth: 0,
  maxWidth: '350px',
  width: '100%',
  '@container (min-width: 600px)': {
    minWidth: '200px',
    width: 'auto',
  },
};

const segmentAndGridContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-6xl'],
  alignItems: 'stretch',
  '@container (min-width: 600px)': {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
});

const legendSx: SxProps<Theme> = ({ backrs }) => ({
  paddingBottom: backrs.spacing['spacing-2xl'],
});

const noPathsContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
};
